import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { CreateUserData } from '@/types';

interface TeamCode {
  teamId: number;
  teamName: string;
  coach_code: string;
  athlete_code: string;
}

interface CreateUserModalProps {
  onClose: () => void;
  /** Optional external handler; if omitted the modal calls the API itself */
  onCreate?: (userData: CreateUserData) => Promise<boolean>;
  /** Called after a successful create (e.g. to refresh data) */
  onSuccess?: () => void;
}

/**
 * Self-contained modal for creating new users.
 * Calls /api/teams/admin/create-user/ directly when no onCreate prop is supplied.
 */
const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreate, onSuccess }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'athlete' as 'admin' | 'coach' | 'athlete',
    first_name: '',
    last_name: '',
    team_code: ''
  });
  const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [resolvedRole, setResolvedRole] = useState<'coach' | 'athlete' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [teamCodes, setTeamCodes] = useState<TeamCode[]>([]);
  const [autoFillCode, setAutoFillCode] = useState(false);
  const [selectedTeamIdx, setSelectedTeamIdx] = useState(0);
  const [canAutoFill, setCanAutoFill] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access');
    const userStr = localStorage.getItem('user_info');
    if (!token || !userStr) return;

    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'coach' && user.role !== 'admin') return;
    } catch { return; }

    fetch(getApiUrl('/api/teams/teams/'), {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
        const teams = Array.isArray(data) ? data : (data.results ?? data.data ?? []);
        const codes: TeamCode[] = teams
          .filter((t: any) => t.coach_code || t.athlete_code)
          .map((t: any) => ({
            teamId: t.id,
            teamName: t.name,
            coach_code: t.coach_code || '',
            athlete_code: t.athlete_code || ''
          }));
        setTeamCodes(codes);
        setCanAutoFill(codes.length > 0);
      })
      .catch(() => {});
  }, []);

  const defaultCreate = async (userData: CreateUserData): Promise<boolean> => {
    const token = localStorage.getItem('access');
    if (!token) throw new Error('Authentication required. Please log in.');
    const response = await fetch(getApiUrl('/api/teams/admin/create-user/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `HTTP ${response.status}`);
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.username || !formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const handler = onCreate ?? defaultCreate;
      const ok = await handler({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: resolvedRole ?? formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name,
        team_code: formData.team_code || undefined
      } as CreateUserData);
      if (ok) {
        setSuccess('User created successfully.');
        onSuccess?.();
        setTimeout(() => onClose(), 1200);
      } else {
        setError('Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while creating the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">
              Create New User
            </h3>
            <p className="cgm-text-secondary text-sm">
              Add a new user to the system
            </p>
          </div>
          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
          )}
          {success && (
            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium cgm-text-secondary mb-1">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="First name"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium cgm-text-secondary mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium cgm-text-secondary mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium cgm-text-secondary mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Re-enter password"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium cgm-text-secondary mb-1">
                Role
              </label>
              <select
                id="role"
                value={resolvedRole ?? formData.role}
                onChange={(e) => {
                  const newRole = e.target.value as 'admin' | 'coach' | 'athlete';
                  setFormData(prev => ({ ...prev, role: newRole }));
                  if (autoFillCode && teamCodes.length > 0 && newRole !== 'admin') {
                    const team = teamCodes[selectedTeamIdx];
                    const code = newRole === 'coach' ? team.coach_code : team.athlete_code;
                    setFormData(prev => ({ ...prev, role: newRole, team_code: code }));
                    setResolvedRole(newRole === 'coach' ? 'coach' : 'athlete');
                    setCodeStatus('valid');
                  } else if (autoFillCode && newRole === 'admin') {
                    setAutoFillCode(false);
                    setFormData(prev => ({ ...prev, role: newRole, team_code: '' }));
                    setResolvedRole(null);
                    setCodeStatus('idle');
                  }
                }}
                disabled={loading || (codeStatus === 'valid' && !autoFillCode)}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {canAutoFill && formData.role !== 'admin' && (
              <div className="flex items-start gap-3">
                <input
                  id="autoFillCode"
                  type="checkbox"
                  checked={autoFillCode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAutoFillCode(checked);
                    if (checked && teamCodes.length > 0) {
                      const team = teamCodes[selectedTeamIdx];
                      const code = formData.role === 'coach' ? team.coach_code : team.athlete_code;
                      setFormData(prev => ({ ...prev, team_code: code }));
                      setResolvedRole(formData.role === 'coach' ? 'coach' : 'athlete');
                      setCodeStatus('valid');
                    } else {
                      setFormData(prev => ({ ...prev, team_code: '' }));
                      setResolvedRole(null);
                      setCodeStatus('idle');
                    }
                  }}
                  disabled={loading}
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-purple-600 cursor-pointer disabled:cursor-not-allowed"
                />
                <label htmlFor="autoFillCode" className="text-sm cgm-text-secondary cursor-pointer select-none">
                  Add to my team
                  {teamCodes.length > 1 && (
                    <select
                      value={selectedTeamIdx}
                      onChange={(e) => {
                        const idx = Number(e.target.value);
                        setSelectedTeamIdx(idx);
                        if (autoFillCode) {
                          const team = teamCodes[idx];
                          const code = formData.role === 'coach' ? team.coach_code : team.athlete_code;
                          setFormData(prev => ({ ...prev, team_code: code }));
                          setResolvedRole(formData.role === 'coach' ? 'coach' : 'athlete');
                          setCodeStatus('valid');
                        }
                      }}
                      disabled={loading}
                      className="ml-1 cgm-input px-1 py-0.5 text-sm rounded disabled:opacity-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {teamCodes.map((tc, i) => (
                        <option key={tc.teamId} value={i}>{tc.teamName}</option>
                      ))}
                    </select>
                  )}
                  {teamCodes.length === 1 && (
                    <span className="font-medium cgm-text-primary"> ({teamCodes[0].teamName})</span>
                  )}
                </label>
              </div>
            )}

            <div className="relative">
              <label htmlFor="teamCode" className="block text-sm font-medium cgm-text-secondary mb-1">
                Athlete/Coach Code (optional)
              </label>
              <input
                id="teamCode"
                type="text"
                value={formData.team_code}
                onChange={async (e) => {
                  const value = e.target.value;
                  setFormData({...formData, team_code: value});
                  setAutoFillCode(false);
                  setResolvedRole(null);
                  if (!value) { setCodeStatus('idle'); return; }
                  setCodeStatus('loading');
                  try {
                    const token = localStorage.getItem('access');
                    const res = await fetch(getApiUrl(`/api/teams/admin/validate-team-code/?code=${encodeURIComponent(value)}`), {
                      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
                    });
                    const data = await res.json();
                    if (data.valid && (data.role === 'coach' || data.role === 'athlete')) {
                      setResolvedRole(data.role);
                      setCodeStatus('valid');
                    } else {
                      setResolvedRole(null);
                      setCodeStatus('invalid');
                    }
                  } catch (err) {
                    setResolvedRole(null);
                    setCodeStatus('invalid');
                  }
                }}
                disabled={loading || autoFillCode}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder={'Enter join code (optional)'}
              />
              <div className="absolute right-3 top-11.5 -translate-y-1/2 flex items-center pointer-events-none">
                {codeStatus === 'loading' && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-500"></div>
                )}
                {codeStatus === 'valid' && (
                  <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {codeStatus === 'invalid' && (
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || (formData.team_code ? codeStatus !== 'valid' : false)}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Creating User...
                </>
              ) : (
                'Create User'
              )}
            </button>
          </form>

          <div className="mt-6 space-y-4 text-center">
            <button
              onClick={onClose}
              className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
